import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { addedToCartEvents } from '../shared/cart-events';
import {
  countRequiredExtraGroups,
  OptionModalData,
  toRenderedExtraList,
} from '../shared/extras';
import { RequestService } from '../service/request.service';
import { GetterService } from '../service/getter-service.service';
import { RecipeService } from '../service/recipe.service';
import { PosthogService } from '../analytics/services/posthog.service';
import { RecipeOptionModalState } from './recipe-option-modal.component';

@Injectable({ providedIn: 'root' })
export class RecipeOptionModalService {
  private readonly modalStateSubject =
    new BehaviorSubject<RecipeOptionModalState | null>(null);

  readonly modalState$: Observable<RecipeOptionModalState | null> =
    this.modalStateSubject.asObservable();

  /**
   * Fires once per line added to the cart (the saved recipe / product line),
   * by this modal or by the legacy extras popup — both write to the same
   * Subject (shared/cart-events.ts). Plain Subject: listeners (shop, search,
   * favourites, checkouts, recommendations, legacy menu) must not get a
   * replay of the previous add when they subscribe.
   */
  private readonly addedToCart = addedToCartEvents;

  readonly addedToCart$: Observable<any> = this.addedToCart.asObservable();

  constructor(
    private requestService: RequestService,
    private localStorage: GetterService,
    private posthog: PosthogService,
    private recipeService: RecipeService
  ) {}

  open(input: any): void {
    const normalized = this.normalizeInputToState(input);
    if (normalized) {
      this.modalStateSubject.next(normalized);
    }
  }

  /**
   * Open the modal for a recipe, flattening its RecipeExtraObject into the
   * renderedExtraList shape the modal expects. Moved here from
   * cards/new-recipe so the promotion card can open the same modal for a free
   * dish without duplicating the parsing.
   */
  openForRecipe(
    recipe: any,
    opts: {
      dineIn?: boolean;
      isPromotionItem?: boolean;
      promotionItemId?: number;
    } = {}
  ): void {
    this.open(this.buildRecipePopupData(recipe, opts));
  }

  buildRecipePopupData(
    recipe: any,
    opts: {
      dineIn?: boolean;
      isPromotionItem?: boolean;
      promotionItemId?: number;
    } = {}
  ): OptionModalData {
    let renderedExtraList: Array<any> = [];
    let renderExtralistChooseOnes = 0;
    let extras: any = recipe?.RecipeExtraObject;

    const toPopupData = (): OptionModalData => ({
      MenuBuilder: extras?.MenuBuilder,
      eatIn: recipe?.ForEatIn,
      dineIn: opts.dineIn,
      filteredProduct: null,
      filteredRecipe: recipe,
      renderedExtraList: renderedExtraList,
      openForNonExtras: false,
      renderExtralistChooseOnes: renderExtralistChooseOnes,
      mergePopups: extras?.mergePopups,
      renderedMenuBuilder: extras?.Sections,
      isPromotionItem: opts.isPromotionItem ?? false,
      PromotionItemId: opts.promotionItemId ?? recipe?.PromotionItemId,
    });

    if (!extras) {
      return toPopupData();
    }

    if (typeof extras == 'string') {
      extras = JSON.parse(extras);
    }

    if (extras?.MenuBuilder && !extras.mergePopups) {
      renderedExtraList = extras?.Sections;
    } else {
      // builds the groups (with MaxQuantity) the popup resolves selection criteria on by key
      // (menu-builder `Sections` are skipped by the helper when popups are merged)
      renderedExtraList = toRenderedExtraList(extras);
      renderExtralistChooseOnes = countRequiredExtraGroups(renderedExtraList);
    }

    return toPopupData();
  }

  normalizeInputToState(input: any): RecipeOptionModalState | null {
    if (!input) {
      return null;
    }

    return this.toRecipeOptionModalState(input as OptionModalData);
  }

  toRecipeOptionModalState(
    data: OptionModalData | null,
    overrides: Partial<RecipeOptionModalState> = {}
  ): RecipeOptionModalState | null {
    if (!data) {
      return null;
    }

    const item = data.filteredRecipe ?? data.filteredProduct;
    if (!item) {
      return null;
    }

    const normalizedItem = this.toDirectItemShape(item, data);
    const extraGroups = this.toExtraGroups(data, normalizedItem);
    const menuBuilderSections =
      data.renderedMenuBuilder ?? this.getMenuBuilderSections(normalizedItem);

    return {
      show: true,
      isOpenForRecipe: !!data.filteredRecipe,
      isDineIn: !!data.dineIn,
      isMenuBuilder: !!data.MenuBuilder,
      mergePopups: !!data.mergePopups,
      item: normalizedItem,
      quantity: data.quantity ?? 1,
      extraGroups,
      menuBuilderSections,
      selectedExtras: [],
      // a promotion item is free — popup.component.ts zeroes recipePrice and
      // recipeTotal the same way, so extras are still charged but the dish is not
      totalPrice: normalizedItem?.isPromotionItem
        ? 0
        : data.filteredRecipe
          ? Number(data.filteredRecipe.RecipePrice ?? 0)
          : Number(data.filteredProduct?.ProductOldPrice ?? 0),
      maxBuyAllowed: data.filteredRecipe?.RecipeMaxBuyAllowed,
      showMaxLimitReached: false,
    };
  }

  private toDirectItemShape(item: any, legacyData?: OptionModalData): any {
    if (!item) {
      return item;
    }

    const direct = { ...item };
    direct.isPromotionItem =
      legacyData?.isPromotionItem ?? item.isPromotionItem ?? false;
    // the cart keys promotion lines on PromotionItemId, so it must survive the
    // OptionModalData -> modal-state hop rather than relying on the spread above
    direct.PromotionItemId =
      legacyData?.PromotionItemId ?? item.PromotionItemId;
    const rawExtraObject =
      item.RecipeExtraObject ?? legacyData?.filteredRecipe?.RecipeExtraObject;

    if (typeof rawExtraObject === 'string') {
      try {
        const parsed = JSON.parse(rawExtraObject);
        if (parsed && typeof parsed === 'object') {
          direct.RecipeExtraObject = parsed;
        }
      } catch {
        direct.RecipeExtraObject = rawExtraObject;
      }
    }

    return direct;
  }

  private toExtraGroups(data: OptionModalData | null, item: any): any[] {
    if (!data) {
      return this.toExtraGroupsFromItem(item);
    }

    const legacyGroups = data.renderedExtraList ?? [];
    if (legacyGroups.length) {
      // a group with no options (an empty "Select Option N" the Menu Builder
      // editor left behind) is dropped — see toRenderedExtraGroup
      return this.withOptions(legacyGroups.map((group) => ({
        Key: group?.Key ?? group?.Title ?? 'Extras',
        extras: Array.isArray(group?.extras)
          ? group.extras.map((extra) => ({
              ...extra,
              Title: extra?.Title ?? extra?.Item,
              Item: extra?.Item ?? extra?.Title,
            }))
          : Array.isArray(group?.Choose)
            ? group.Choose.map((extra) => ({
                ...extra,
                Title: extra?.Title ?? extra?.Item,
                Item: extra?.Item ?? extra?.Title,
              }))
            : [],
        maxQuantity: this.getExtraGroupMaxQuantity(group),
        isMenuBuilderSection: false,
      })));
    }

    return this.toExtraGroupsFromItem(item);
  }

  /** Drops extras groups that have nothing to choose from. */
  private withOptions(groups: any[]): any[] {
    return groups.filter((group) => group?.extras?.length);
  }

  private toExtraGroupsFromItem(item: any): any[] {
    if (!item) {
      return [];
    }

    const rawExtras = item.RecipeExtraObject ?? item.ProductExtraObject;
    if (!rawExtras) {
      return [];
    }

    const parsedExtras =
      typeof rawExtras === 'string' ? this.safeJsonParse(rawExtras) : rawExtras;

    if (!parsedExtras || typeof parsedExtras !== 'object') {
      return [];
    }

    if (parsedExtras.MenuBuilder && Array.isArray(parsedExtras.Sections)) {
      return parsedExtras.Sections.map((section: any) => ({
        Key: section?.Title ?? section?.Key ?? 'Extras',
        extras: Array.isArray(section?.Choose) ? section.Choose : [],
        maxQuantity: section?.maxQuantity,
        isMenuBuilderSection: true,
      }));
    }

    const groups: any[] = [];
    Object.entries(parsedExtras as Record<string, any>).forEach(
      ([key, value]) => {
        if (
          typeof value === 'boolean' ||
          key === 'Sections' ||
          key === 'MenuBuilder'
        ) {
          return;
        }

        const extrasValue = value as Record<string, any> | any[] | undefined;
        const items = Array.isArray(extrasValue)
          ? extrasValue
          : ((extrasValue as Record<string, any>)?.['Extras'] ?? []);
        groups.push({
          Key: key,
          extras: Array.isArray(items)
            ? items.map((extra) => ({
                ...extra,
                Title: extra?.Title ?? extra?.Item,
                Item: extra?.Item ?? extra?.Title,
              }))
            : [],
          maxQuantity: this.getExtraGroupMaxQuantity(extrasValue),
        });
      }
    );

    return this.withOptions(groups);
  }

  private getExtraGroupMaxQuantity(group: any): number | undefined {
    const configuredMax = group?.maxQuantity ?? group?.MaxQuantity;
    const maxQuantity = Number(configuredMax);

    return Number.isFinite(maxQuantity) && maxQuantity > 0
      ? maxQuantity
      : undefined;
  }

  private getMenuBuilderSections(item: any): any[] {
    if (!item) {
      return [];
    }

    const rawExtras = item.RecipeExtraObject ?? item.ProductExtraObject;
    if (!rawExtras) {
      return [];
    }

    const parsedExtras =
      typeof rawExtras === 'string' ? this.safeJsonParse(rawExtras) : rawExtras;

    if (
      parsedExtras &&
      parsedExtras.MenuBuilder &&
      Array.isArray(parsedExtras.Sections)
    ) {
      return parsedExtras.Sections;
    }

    return [];
  }

  private safeJsonParse(value: any): any {
    if (!value || typeof value !== 'string') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  normalizeLegacyExtraArray(extras: any[] = []): any[] {
    return extras.map((extra) => ({
      Image: extra?.Image ?? extra?.Img ?? '',
      Key: extra?.Key ?? extra?.cat ?? 'Extras',
      Price: Number(extra?.Price ?? extra?.ExtraPrice ?? 0),
      Quantity: Number(extra?.Quantity ?? 1),
      Title: extra?.Title ?? extra?.Item ?? extra?.name ?? '',
      cat: extra?.cat ?? extra?.Key ?? 'Extras',
      Id: extra?.Id,
      redirection: extra?.redirection,
      RecipeOldPrice: extra?.RecipeOldPrice,
      RecipeDiscountType: extra?.RecipeDiscountType,
      RecipeDiscountAmount: extra?.RecipeDiscountAmount,
      checked: extra?.checked,
      maxQuantity: extra?.maxQuantity,
    }));
  }

  addSelectedItemToCart(
    state: RecipeOptionModalState | null,
    payload: any
  ): void {
    if (!state || !payload) {
      return;
    }

    const selectedItem = state.item;
    const flag = state.isOpenForRecipe ? 'recipe' : 'product';
    const normalizedExtras = this.normalizeLegacyExtraArray(
      payload.extras ?? []
    );
    const recipeQuantity = Number(payload.quantity ?? state.quantity ?? 1);
    const extrasTotal = normalizedExtras.reduce(
      (sum, current) =>
        sum + Number(current.Price ?? 0) * Number(current.Quantity ?? 1),
      0
    );

    let data: any;
    let existing: any[] = [];

    if (flag === 'recipe') {
      const isPromotionItem = !!selectedItem?.isPromotionItem;
      // free dish: the recipe costs nothing, chosen extras still do
      const itemPrice = isPromotionItem
        ? 0
        : Number(selectedItem?.RecipePrice ?? 0);
      const recipeTotal =
        itemPrice * recipeQuantity + extrasTotal * recipeQuantity;

      // order-type flags: the recipe's AND its category's (a dish in a
      // category not offered for eat-in isn't available for eat-in)
      const availability = this.recipeService.availabilityOf(
        selectedItem.RecipeId,
        selectedItem.FK_CategoryId_Recipes
      );

      data = {
        RecipeId: selectedItem.RecipeId,
        RecipeTitle: selectedItem.RecipeTitle,
        RecipePrice: itemPrice + extrasTotal,
        recipeTotal,
        RecipeImage: selectedItem.RecipeImage,
        RecipeQuantity: recipeQuantity,
        IsDiscountedItem: selectedItem.isDiscountedItem,
        RecipeExtraObject: normalizedExtras,
        socialDiscountItem: selectedItem.socialDiscountItem,
        ForDelivery: availability?.ForDelivery ?? selectedItem?.ForDelivery,
        ForCollection:
          availability?.ForCollection ?? selectedItem?.ForCollection,
        ForDineIn: availability?.ForDineIn ?? selectedItem?.ForDineIn,
        ForEatIn: availability?.ForEatIn ?? selectedItem?.ForEatIn,
        RecipeMaxBuyAllowed: selectedItem.RecipeMaxBuyAllowed,
        CategoryTitle: selectedItem.CategoryTitle,
        // the legacy popup stores it too; a dine-in table order groups lines by it
        CategoryId: selectedItem.FK_CategoryId_Recipes,
        RecipeOldPrice: selectedItem.RecipeOldPrice,
        RecipeDiscountType: selectedItem.RecipeDiscountType,
        RecipeDiscountAmount: selectedItem.RecipeDiscountAmount,
        isPromotionItem: selectedItem.isPromotionItem,
        PromotionItemId: selectedItem.PromotionItemId,
      };

      existing = JSON.parse(this.localStorage.get('cartRecipeItems') || '[]');

      if (existing?.length) {
        let match = false;
        if (
          !selectedItem.isDiscountedItem ||
          !selectedItem.socialDiscountItem
        ) {
          existing.forEach((val) => {
            if (val.socialDiscountItem) return;
            // a free dish only ever matches the same promotion; a paid dish only
            // ever matches a paid line, so the two never collapse into one
            const sameLine = isPromotionItem
              ? val.isPromotionItem &&
                val.PromotionItemId == selectedItem.PromotionItemId &&
                val.RecipeId == selectedItem.RecipeId
              : val.RecipeId == selectedItem.RecipeId && !val.isPromotionItem;

            if (sameLine) {
              // same extras AND same extra quantities → bump the line; otherwise it's a new line
              if (
                this.requestService.sameExtras(
                  val.RecipeExtraObject,
                  normalizedExtras
                )
              ) {
                match = true;
                val.RecipeQuantity = val.RecipeQuantity + recipeQuantity;
                val.IsDiscountedItem = selectedItem.IsDiscountedItem;
                val.recipeTotal =
                  val.recipeTotal + val.RecipePrice * recipeQuantity;
              }
            }
          });
        }

        if (!match) {
          existing.push(data);
        }

        this.requestService.saveRecipeCartItems(existing);
      } else {
        const tmpArray = [data];
        this.localStorage.set('cartRecipeItems', JSON.stringify(tmpArray));
        this.requestService.saveRecipeCartItems(tmpArray);
      }
    } else {
      const productPrice = Number(selectedItem?.ProductOldPrice ?? 0);
      const productTotal =
        productPrice * recipeQuantity + extrasTotal * recipeQuantity;

      data = {
        ProductId: selectedItem.ProductId,
        ProductTitle: selectedItem.ProductTitle,
        ProductOldPrice: productPrice + extrasTotal,
        ProductNewPrice: selectedItem.ProductNewPrice,
        ProductTotal: productTotal,
        ProductImageUrl: selectedItem.ProductImageUrl,
        ProductUnits: selectedItem?.ProductUnits,
        IsDiscountedItem: selectedItem.isDiscountedItem,
        ProductQuantity: recipeQuantity,
        ProductExtraObject: normalizedExtras,
        ForDelivery: selectedItem?.ForDelivery,
        ForCollection: selectedItem?.ForCollection,
      };

      existing = JSON.parse(this.localStorage.get('cartProductItems') || '[]');

      if (existing?.length) {
        let match = false;
        if (!selectedItem.isDiscountedItem) {
          existing.forEach((val) => {
            if (val.ProductId == selectedItem.ProductId) {
              if (
                this.requestService.sameExtras(
                  val.ProductExtraObject,
                  normalizedExtras
                )
              ) {
                val.ProductTotal =
                  val.ProductTotal + productPrice * recipeQuantity;
                val.ProductQuantity = val.ProductQuantity + recipeQuantity;
                match = true;
              }
            }
          });
        }

        if (!match) {
          existing.push(data);
        }

        this.requestService.saveProductCartItems(existing);
      } else {
        const tmpArray = [data];
        this.localStorage.set('cartProductItems', JSON.stringify(tmpArray));
        this.requestService.saveProductCartItems(tmpArray);
      }
    }

    this.addedToCart.next(data);

    this.posthog.captureEvent('Item_Added_to_cart_Successfully', {
      RecipeId: data.RecipeId,
      RecipeTitle: data.RecipeTitle,
      Category: data.CategoryTitle,
      Unit_price: data.RecipePrice,
      Quantity: data.RecipeQuantity,
    });

    if (typeof (window as any).fbq !== 'undefined') {
      (window as any).fbq('track', 'Add to cart', {
        item: selectedItem.RecipeTitle || selectedItem.ProductTitle,
      });
    }
  }
}
